import { useQuery } from 'react-query';
import { getConsolidatedSectionData } from '../services/sectionAvatarService';
import useNotification from './useNotification';

/**
 * Custom hook for project-specific section data with avatars and orchestrators
 * This replaces multiple API calls with a single consolidated call for better performance
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.projectId - The project ID
 * @param {boolean} options.activeOnly - Whether to only fetch active sections
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @returns {Object} Section state and methods with consolidated data
 */
const useProjectSections = ({ 
  projectId,
  activeOnly = true,
  enabled = true
} = {}) => {
  const { showNotification } = useNotification();
  
  // Get consolidated section data (sections + avatars + orchestrators) in a single call
  const {
    data: consolidatedData,
    isLoading: isLoadingSections,
    error: sectionsError,
    refetch: refetchSections,
    isFetching
  } = useQuery(
    ['project-sections-consolidated', projectId, { active: activeOnly }],
    () => {
      if (!projectId) {
        return Promise.resolve({ success: false, data: [], message: 'No project ID provided' });
      }
      
      console.log('useProjectSections: Fetching consolidated section data for project:', projectId);
      return getConsolidatedSectionData(projectId, activeOnly);
    },
    {
      enabled: enabled && !!projectId,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry if the consolidated endpoint doesn't exist yet
        if (error?.response?.status === 404) {
          console.log('useProjectSections: Consolidated endpoint not found, falling back to individual calls');
          return false;
        }
        return failureCount < 2; // Retry 2 times for other errors
      },
      staleTime: 30000, // Consider data fresh for 30 seconds (shorter than regular sections)
      cacheTime: 300000, // Keep in cache for 5 minutes
      onSuccess: (data) => {
        if (data?.success) {
          console.log('useProjectSections: Successfully loaded consolidated data:', {
            count: data.count,
            metadata: data.metadata
          });
        } else if (data?.fallback) {
          console.log('useProjectSections: Consolidated API failed, need fallback implementation');
        }
      },
      onError: (error) => {
        console.error('useProjectSections: Error loading consolidated section data:', error);
        
        // Only show notification for real errors, not 404s (endpoint might not exist yet)
        if (error?.response?.status !== 404) {
          showNotification({ 
            message: `Failed to load section data: ${error.message}`, 
            type: 'error' 
          });
        }
      }
    }
  );
  
  // Process the consolidated data
  const sections = consolidatedData?.success ? consolidatedData.data : [];
  const hasConsolidatedData = consolidatedData?.success && !consolidatedData?.fallback;
  
  // Helper function to get section avatar data
  const getSectionAvatar = (sectionId) => {
    const section = sections.find(s => s.id === sectionId || s._id === sectionId);
    return section?.avatar || { url: null, name: null };
  };
  
  // Helper function to get section orchestrator data
  const getSectionOrchestrator = (sectionId) => {
    const section = sections.find(s => s.id === sectionId || s._id === sectionId);
    return section?.orchestrator || { 
      name: null, 
      role: 'Orchestrator', 
      avatar_url: null, 
      assigned: false 
    };
  };
  
  // Helper function to check if we have complete data for a section
  const hasSectionData = (sectionId) => {
    const section = sections.find(s => s.id === sectionId || s._id === sectionId);
    return !!(section && hasConsolidatedData);
  };
  
  // Helper to get all task IDs organized by section
  const getTaskIdsBySection = () => {
    if (!sections.length) return {};
    
    const taskIds = {};
    sections.forEach(section => {
      const sectionId = section.id || section._id;
      if (section.tasks && section.tasks.length > 0) {
        taskIds[sectionId] = section.tasks.map(task => task.id || task._id);
      } else {
        taskIds[sectionId] = [];
      }
    });
    
    return taskIds;
  };
  
  // Helper to get all tasks in a flat array
  const getAllTasks = () => {
    if (!sections.length) return [];
    
    const allTasks = [];
    sections.forEach(section => {
      if (section.tasks && section.tasks.length > 0) {
        allTasks.push(...section.tasks);
      }
    });
    
    return allTasks;
  };

  return {
    // Consolidated section data
    sections,
    isLoadingSections,
    sectionsError,
    refetchSections,
    isFetching,
    
    // Data completeness flags
    hasConsolidatedData,
    needsFallback: consolidatedData?.fallback || false,
    
    // Helper functions for accessing consolidated data
    getSectionAvatar,
    getSectionOrchestrator,
    hasSectionData,
    getTaskIdsBySection,
    getAllTasks,
    
    // Metadata
    metadata: consolidatedData?.metadata,
    count: consolidatedData?.count || sections.length
  };
};

export default useProjectSections;